import { DateOnly } from "@anna-money/anna-web-lib/utils";
import { parseDdmmyyyy } from "helpers/date";
import { type ValidationError, type Validator, type ValidatorValue } from "./types";

const NUMBERS_ONLY_REGEX = /^[0-9]+$/;
const DATE_REGEX = /(^0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4}$)/;
// Companies House personal code: XXX-XXXX-XXXX format, must contain both letters and digits
const CH_PERSONAL_CODE_REGEX = /^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]{3}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

const stringValidator =
  <T = string>(condition: (value: ValidatorValue<T>) => boolean, errorMsg: string): Validator<T> =>
  (value): ValidationError => {
    const valueToCheck = typeof value === "string" ? value.trim() : value;

    return condition(valueToCheck as T) ? errorMsg : undefined;
  };

export const required = (errorMsg: string = "Please complete this field"): Validator<string> =>
  stringValidator((value) => !value, errorMsg);

export const emailOnly = (errorMsg: string = "Please enter a valid email address"): Validator<string> =>
  stringValidator((value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)), errorMsg);

export const emailUnique = (
  existingEmails: string[],
  errorMsg: string = "This email is already used by another director",
): Validator<string> =>
  stringValidator(
    (value) => !!value && existingEmails.some((email) => email.toLowerCase() === String(value).toLowerCase()),
    errorMsg,
  );

export const mobileUnique = (
  existingMobiles: string[],
  errorMsg: string = "This mobile number is already used by another director",
): Validator<string> =>
  stringValidator((value) => {
    if (!value) {
      return false;
    }
    // Normalize by removing hyphens for comparison
    const normalizedValue = String(value).replace(/-/g, "");
    return existingMobiles.some((mobile) => mobile.replace(/-/g, "") === normalizedValue);
  }, errorMsg);

export const nameUnique = (
  existingNames: Array<{ firstName?: string; lastName?: string }>,
  getOtherFieldValue: () => string | undefined,
  fieldType: "firstName" | "lastName",
  errorMsg: string = "This name combination is already used by another director",
): Validator<string> =>
  stringValidator((value) => {
    if (!value) {
      return false;
    }
    const otherValue = getOtherFieldValue();
    if (!otherValue) {
      return false;
    }

    const firstName = fieldType === "firstName" ? String(value) : otherValue;
    const lastName = fieldType === "lastName" ? String(value) : otherValue;

    return existingNames.some(
      (name) =>
        name.firstName?.toLowerCase() === firstName.toLowerCase() &&
        name.lastName?.toLowerCase() === lastName.toLowerCase(),
    );
  }, errorMsg);

export const numbersOnly = (errorMsg: string = "Please enter only numbers"): Validator<string> =>
  stringValidator((value) => (value ? !String(value).match(NUMBERS_ONLY_REGEX) : false), errorMsg);

export const validDate = (errorMsg: string = "Please enter date in format DD/MM/YYYY"): Validator<string> =>
  stringValidator((value) => !String(value).match(DATE_REGEX), errorMsg);

export const isOver18 = (errorMsg: string = "You can register a business once you're 18 or older"): Validator<string> =>
  stringValidator((dob) => {
    if (!dob) {
      return true;
    }

    const date = parseDdmmyyyy(dob);
    if (!date) {
      // Let validDate report format errors; don't block partially-typed input.
      return false;
    }

    return date.isAfter(DateOnly.today.subYears(18));
  }, errorMsg);

export const booleanOnly = (errorMsg: string = "Please choose an option"): Validator<boolean> =>
  stringValidator<boolean>((value) => typeof value !== "boolean", errorMsg);

export const exactLength = (length: number, errorMsg: string = "Please check your entry"): Validator<string> =>
  stringValidator((value) => !value || value?.length !== length, errorMsg);

export const chPersonalCodeFormat = (
  errorMsg: string = "Enter a valid code in the format XXX-XXXX-XXXX. It must include letters and numbers",
): Validator<string> =>
  stringValidator((value) => (value ? !String(value).match(CH_PERSONAL_CODE_REGEX) : false), errorMsg);

const normalizeChPersonalCode = (code: string): string => code.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

export const chPersonalCodeUnique = (
  otherDirectorsCodes: string[],
  errorMsg: string = "This code is already used by another director",
): Validator<string> =>
  stringValidator((value) => {
    if (!value) {
      return false;
    }
    const normalizedValue = normalizeChPersonalCode(String(value));
    return otherDirectorsCodes.some((code) => normalizeChPersonalCode(code) === normalizedValue);
  }, errorMsg);

export const minLength = (
  length: number,
  errorMsg: string = `Please enter at least ${length} characters`,
): Validator<string> => stringValidator((value) => !!value && (value?.length || 0) < length, errorMsg);

export const noForbiddenChars = (
  chars: string[],
  errorMsg: string = `The following characters are not allowed: ${chars.join(", ")}`,
): Validator<string> =>
  stringValidator((value) => !!value && chars.some((char) => String(value).includes(char)), errorMsg);

export const minArrayLength = (length: number, errorMsg: string = `Min length ${length}`): Validator<unknown[]> =>
  stringValidator((value) => !!value && (value?.length || 0) < length, errorMsg);

export const maxArrayLength = (length: number, errorMsg: string = `Max length ${length}`): Validator<unknown[]> =>
  stringValidator((value) => !!value && (value?.length || 0) > length, errorMsg);

export const minValue = (minValue: number, errorMsg: string = `Min value is ${length}`): Validator<string> =>
  stringValidator((value) => {
    const parsedValue = value ? parseInt(value) : 0;
    return parsedValue < minValue;
  }, errorMsg);

export const onlyTrue = (errorMsg: string = "Please fill in this field"): Validator<string> =>
  stringValidator((value) => !value, errorMsg);

export const composeValidators =
  <T = string>(...validators: Array<Validator<T>>) =>
  (value: ValidatorValue<T>): ValidationError =>
    validators.reduce<ValidationError>((error, validatorFn) => error || validatorFn(value), undefined);

type StyleModule = Record<string, string>;

const toCamelCase = (str: string): string => str.replace(/-([a-z])/g, (_, char) => char.toUpperCase());

export const getStyle = (styles: StyleModule, prefix: string, value?: string | null | number): string | undefined => {
  if (!value) {
    return undefined;
  }
  const rawKey = `${prefix}-${value}`; // e.g., weight-regular
  const camelKey = toCamelCase(rawKey); // weightRegular
  return styles[camelKey] ?? undefined;
};

export const getCamelCaseStyle = (
  styles: StyleModule,
  prefix: string,
  value?: string | null | number,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  const rawKey = `${prefix}${value}`; // e.g., weight-regular
  const camelKey = toCamelCase(rawKey); // weightRegular
  return styles[camelKey] ?? undefined;
};

export { validateTfn } from "./tfn";
